export type EntityLevel = 'CARRIER' | 'FINANCIAL_GROUP' | 'CONTRACT' | 'SUB_CONTRACT';

export interface BaseEntity { id: number }
export interface Carrier extends BaseEntity { xippCommercialName?: string }
export interface FinancialGroup extends BaseEntity { name?: string }
export interface Contract extends BaseEntity { name?: string }
export interface SubContract extends BaseEntity { name?: string }

export interface SubContractPair {
  contractId: number | string;
  subContractId?: number | string;
}

export interface EntityIds {
  carrierIds?: Array<number | string>;
  financialGroupIds?: Array<number | string>;
  contractIds?: Array<number | string>;
  subContractIds?: SubContractPair[];
}

export interface Config {
  entityLevel: EntityLevel;
  carrierId?: number | string;
  financialGroupId?: number | string;
  contractId?: number | string;
  subContractId?: number | string;
  valueType?: string;
  value?: string[] | string | number | boolean | null;
  possibleValues?: any[] | [];
  name?: string;
}

export interface ConfigWithName extends Config {
  entityLevelName: string;
}

export interface CarrierServiceLike {
  FindByIds(ids: number[]): Promise<{ data?: Carrier[] } | undefined>;
}

export interface FinancialGroupServiceLike {
  FindByIds(ids: number[]): Promise<{ data?: FinancialGroup[] } | undefined>;
}

export interface ContractServiceLike {
  FindByIds(ids: number[]): Promise<{ data?: Contract[] } | undefined>;
  FindByParentId(parentId: number): Promise<{ data?: SubContract[] } | undefined>;
}

export interface MountEntityConfigsParams {
  entityIds: EntityIds;
  data: Config[];
  services?: {
    carrier?: CarrierServiceLike;
    financialGroup?: FinancialGroupServiceLike;
    contract?: ContractServiceLike;
  };
}

export const toNum = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const toNumArray = (arr?: Array<number | string>): number[] =>
  (arr ?? []).map(toNum).filter((n): n is number => n !== null);

export const unique = (arr: number[]): number[] => Array.from(new Set(arr));

export const buildMap = <T extends BaseEntity>(items: T[] = []): Map<number, T> =>
  new Map(items.map(i => [Number(i.id), i]));

export function resolveEntityLevelName(
  cfg: Config,
  lookups: {
    carriers: Map<number, Carrier>;
    financialGroups: Map<number, FinancialGroup>;
    contracts: Map<number, Contract>;
    subContracts: Map<number, SubContract>;
  }
): string {
  switch (cfg.entityLevel) {
    case 'CARRIER': {
      const id = toNum(cfg.carrierId);
      return id !== null ? (lookups.carriers.get(id)?.xippCommercialName ?? '(Operadora desconhecida)') : '(Operadora desconhecida)';
    }
    case 'FINANCIAL_GROUP': {
      const id = toNum(cfg.financialGroupId);
      return id !== null ? (lookups.financialGroups.get(id)?.name ?? '(Grupo financeiro desconhecido)') : '(Grupo financeiro desconhecido)';
    }
    case 'CONTRACT': {
      const id = toNum(cfg.contractId);
      return id !== null ? (lookups.contracts.get(id)?.name ?? '(Contrato desconhecido)') : '(Contrato desconhecido)';
    }
    case 'SUB_CONTRACT': {
      const rawId = cfg.subContractId;
      const id = toNum(rawId);
      return id !== null ? (lookups.subContracts.get(id)?.name ?? '(Subcontrato desconhecido)') : '(Subcontrato desconhecido)';
    }
    default:
      return '(Nível desconhecido)';
  }
}
